import { createReducer } from '@reduxjs/toolkit';
import { OutputLifecycleSurveyRecipients } from '~/gql/graphql';
import { lifecycleSurveyRecipientsActions } from '~/behaviors/actions/lifecycleSurveyRecipients.actions';
import { handleUnsafeOptionalChaining } from '~/tools/lint/handleUnsafeOptionalChaining';

type LifecycleSurveyRecipientsState = Record<
  string,
  OutputLifecycleSurveyRecipients & {
    addToDoNotContact?: boolean;
    doNotContactReason?: string;
    inviteImmediately?: boolean;
    removeFromList?: boolean;
  }
>;

const defaultState = {};

const replaceLifecycleSurveyRecipients = (
  _state: LifecycleSurveyRecipientsState,
  action: { payload: OutputLifecycleSurveyRecipients[] },
): LifecycleSurveyRecipientsState => {
  const recipients = action?.payload || [];
  const nextState: LifecycleSurveyRecipientsState = {};
  Object.values(recipients).forEach((recipient) => {
    nextState[recipient.id] = { ...recipient };
  });
  return nextState;
};

const updateLifecycleSurveyRecipients = (state: LifecycleSurveyRecipientsState, action) => {
  for (const {
    id,
    addToDoNotContact,
    doNotContactReason,
    inviteImmediately,
    isApprovedToAdd,
    removeFromList,
  } of handleUnsafeOptionalChaining(action?.payload).lifecycleSurveyRecipients) {
    state[id] = {
      ...state[id],
      addToDoNotContact: addToDoNotContact ?? state['addToDoNotContact'],
      doNotContactReason: doNotContactReason ?? state['doNotContactReason'],
      inviteImmediately: inviteImmediately ?? state['inviteImmediately'],
      isApprovedToAdd: isApprovedToAdd ?? state['isApprovedToAdd'],
      removeFromList: removeFromList ?? state['removeFromList'],
    };
  }
  return state;
};

const updateLifecycleSurveyRecipient = (state: LifecycleSurveyRecipientsState, action) => {
  const { id, addToDoNotContact, doNotContactReason, inviteImmediately, isApprovedToAdd, removeFromList } =
    handleUnsafeOptionalChaining(action?.payload);
  state[id] = {
    ...state[id],
    addToDoNotContact: addToDoNotContact ?? state['addToDoNotContact'],
    doNotContactReason: doNotContactReason ?? state['doNotContactReason'],
    inviteImmediately: inviteImmediately ?? state['inviteImmediately'],
    isApprovedToAdd: isApprovedToAdd ?? state['isApprovedToAdd'],
    removeFromList: removeFromList ?? state['removeFromList'],
  };
  return state;
};

const approveAllLifecycleSurveyRecipients = (state: LifecycleSurveyRecipientsState, action) => {
  const { recipientsToApprove } = handleUnsafeOptionalChaining(action?.payload);
  recipientsToApprove.forEach((recipient) => {
    state[recipient.id] = {
      ...state[recipient.id],
      isApprovedToAdd: true,
    };
  });
};

const lifecycleSurveyRecipients = createReducer(defaultState, (builder) => {
  builder
    .addCase(lifecycleSurveyRecipientsActions.getLifecycleSurveyRecipients.fulfilled, replaceLifecycleSurveyRecipients)
    .addCase(lifecycleSurveyRecipientsActions.updateLifecycleSurveyRecipient.fulfilled, updateLifecycleSurveyRecipient)
    .addCase(
      lifecycleSurveyRecipientsActions.updateLifecycleSurveyRecipients.fulfilled,
      updateLifecycleSurveyRecipients,
    )
    .addCase(
      lifecycleSurveyRecipientsActions.approveAllLifecycleSurveyRecipients.fulfilled,
      approveAllLifecycleSurveyRecipients,
    );
});

export default lifecycleSurveyRecipients;
